import axios from '@/plugins/axios'

/**
 * Serviço centralizado para gerenciar alertas do sistema
 * Verifica periodicamente se há alertas a serem exibidos
 */
class AlertService {
  constructor() {
    this.interval = null
    this.checkInterval = 600000 // Verificar a cada 10 minutos
    this.isChecking = false
  }

  /**
   * Inicia o monitoramento de alertas
   * @param {Function} callback - Função chamada quando um alerta é detectado
   */
  startMonitoring(callback) {
    if (this.interval) {
      console.warn('Monitoramento de alertas já está ativo')

      return
    }

    // Verifica imediatamente ao iniciar
    this.checkAlerts(callback)

    // Depois verifica no intervalo configurado
    this.interval = setInterval(() => {
      this.checkAlerts(callback)
    }, this.checkInterval)

    console.log('Monitoramento de alertas iniciado')
  }

  /**
   * Para o monitoramento de alertas
   */
  stopMonitoring() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
      console.log('Monitoramento de alertas parado')
    }
  }

  /**
   * Verifica todos os alertas disponíveis
   * @param {Function} callback - Função chamada quando um alerta é detectado
   */
  async checkAlerts(callback) {
    // Evita múltiplas verificações simultâneas
    if (this.isChecking) {
      return
    }

    this.isChecking = true

    try {
      // Verifica alerta de auditoria
      await this.checkAuditAlert(callback)

      // Verifica alerta de diárias
      await this.checkDailyAlert(callback)

      // Verifica hóspedes com dados incompletos para o checkout
      await this.checkGuestDataAlert(callback)

      // Verifica e executa auditoria automática
      await this.checkAutomaticAudit(callback)

      // Verifica aviso de sistema (criado manualmente pelo desenvolvedor)
      await this.checkSystemNotice(callback)

      // Adicione outros alertas aqui conforme necessário
    } catch (error) {
      console.error('Erro ao verificar alertas:', error)
    } finally {
      this.isChecking = false
    }
  }

  /**
   * Verifica se há alerta de auditoria pendente
   * @param {Function} callback
   */
  async checkAuditAlert(callback) {
    try {
      const response = await axios.post('/alerts/audit_alert')

      if (response.data.success === true) {
        await callback({
          type: 'audit',
          title: 'Auditoria Pendente',
          message: 'A auditoria do dia ainda não foi executada!',
          icon: 'warning',
          action: '/audit',
          actionText: 'Ir para Auditoria',
        })
      }
    } catch (error) {
      console.error('Erro ao verificar alerta de auditoria:', error)
    }
  }

  /**
   * Verifica se há alerta de diárias pendente
   * @param {Function} callback
   */
  async checkDailyAlert(callback) {
    try {
      const response = await axios.post('/alerts/daily_alert')

      if (response.data.success === true) {
        await callback({
          type: 'daily',
          title: 'Diárias Pendentes',
          message: 'A execução de diárias do dia ainda não foi realizada!',
          icon: 'warning',
          action: '/diary_execution',
          actionText: 'Ir para Diárias',
        })
      }
    } catch (error) {
      console.error('Erro ao verificar alerta de diárias:', error)
    }
  }

  /**
   * Verifica se há quartos ocupados com dados de hóspedes incompletos para o checkout
   * @param {Function} callback
   */
  async checkGuestDataAlert(callback) {
    try {
      const response = await axios.post('/alerts/guest_data_alert')

      if (response.data.success === true) {
        const escape = text => String(text)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
        const items = response.data.rooms.map(room => `<li style="margin-bottom:6px"><b>Quarto ${escape(room.room)}</b><br>${room.guests
          .map(guest => `${escape(guest.name)} <small>(falta ${escape(guest.missing.join(', '))})</small>`)
          .join('<br>')}</li>`)

        await callback({
          type: 'guest_data',
          title: 'Dados de Hóspedes Incompletos',
          html: `<p>Preencha corretamente os dados dos hóspedes para não deixar para a última hora:</p><ul style="text-align:left;list-style:none;padding:0">${items.join('')}</ul>`,
          message: response.data.message,
          icon: 'warning',
        })
      }
    } catch (error) {
      console.error('Erro ao verificar alerta de dados de hóspedes:', error)
    }
  }

  /**
   * Verifica e executa auditoria automática se necessário
   * @param {Function} callback
   */
  async checkAutomaticAudit(callback) {
    try {
      const response = await axios.post('/audit/automatic')

      // Se executou com sucesso, exibe notificação
      if (response.data.success === true) {
        await callback({
          type: 'automatic_audit',
          title: 'Auditoria Automática Executada',
          message: 'A auditoria automática foi executada com sucesso!',
          icon: 'success',
          action: '/audit',
          actionText: 'Ver Auditoria',
        })
      }

      // Se não executou (false), não faz nada (é normal)
    } catch (error) {
      // Só loga erros 500, não erros 200 com success: false
      if (error.response && error.response.status >= 500) {
        console.error('Erro ao verificar auditoria automática:', error)
      }
    }
  }

  /**
   * Verifica se há aviso de sistema ativo (janela start_at/end_at ou fechado manualmente)
   * @param {Function} callback
   */
  async checkSystemNotice(callback) {
    try {
      const response = await axios.get('/system-notices/active')

      if (response.data.success === true) {
        await callback({
          type: 'system_notice',
          title: response.data.title || 'Aviso do Sistema',
          message: response.data.message,
          icon: 'info',
        })
      }
    } catch (error) {
      console.error('Erro ao verificar aviso de sistema:', error)
    }
  }

  /**
   * Altera o intervalo de verificação
   * @param {number} milliseconds - Novo intervalo em milissegundos
   */
  setCheckInterval(milliseconds) {
    this.checkInterval = milliseconds

    // Se já está monitorando, reinicia com novo intervalo
    if (this.interval) {
      const wasMonitoring = true
      this.stopMonitoring()

      if (wasMonitoring) {
        // Nota: precisa passar o callback novamente ao reiniciar
        console.warn('Intervalo alterado. Reinicie o monitoramento manualmente.')
      }
    }
  }
}

export default new AlertService()
